import React, { useCallback } from "react";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  Channel,
  forwardEmailQueueStatus,
  ProductFetchingProgress,
  ToastifyStatus,
  TrackerWithRecipientNames,
} from "../../interfaces/interfaces";
import {
  addChannels,
  addNewChannel,
  deleteChannelFromMenu,
  deleteChannelFromNotification,
} from "../../redux/chat/chatSlice";
import {
  addTrackerFromNotification,
  deleteTrackerFromNotification,
  updateTrackerFromNotification,
} from "../../redux/trackers/trackerSlice";
import {
  addSingleNotification,
  changeResetStatus,
} from "../../redux/notifications/notificationSlice";
import { useAppDispatch, useAppSelector } from "../../redux/reduxHooks";
import { refreshAsyncTrackerSummary } from "../../redux/trackers/trackerUtil";
import {
  getLocalJWT,
  getLocalUser,
  getLocalUserId,
} from "../functions/utilities";
import { socketInit } from "./socketInit";
import {
  createWs,
  dataReceived,
  setRefreshMyWorkspace,
  workspaceRefreshed,
} from "../../redux/wsProgress/wsProgress";
import {
  addTempWorkspace,
  toggleIsInProgress,
  setError,
  temporaryUpdate,
  permanentToggleIsInProgress,
  viewSelectRefreshToggle,
  toggleTemp,
} from "../../redux/temporaryWorkspase/temporaryWorkspace";
import { changeDfStatus } from "../../redux/dataFetchingProgress/dataFetchingProgress";
import {
  setForceRefresh,
  setInsightsFromSocket,
  setRefreshMetricsAndIdeas,
} from "../../redux/decisionHub/decisionHub";
import { DecisionInsights } from "../../pages/DecisionHub/interfaces";
import { setInsightGenerated } from "../../redux/goalsIdeasMoves/goalsIdeasMoves";
import {
  fetchProductMissingCosts,
  getMissingCostItemsThunk,
  setBulkUpdateStatus,
  setCloseBulkModal,
  setRefreshAccountFetching,
  setRefreshAccountUpdate,
  setRefreshFinanceStatement,
  setRefreshPcHome,
} from "../../redux/financeStatement/financeStatement";
import {
  addVerifyGstJob,
  setRefreshFormData,
  setRefreshGstJobs,
  setRefreshStatementJobs,
  setRefreshUwHome,
  setRefreshRevenueClassification,
} from "../../redux/underwriting/underwriting";
import { RestS } from "../../rest/rest-service";
import {
  setRefreshDashboard,
  setTemporaryJobDetails,
  updateTemporaryJobDetails,
} from "../../redux/temporaryDashboard/temporaryDashboard";
import {
  changeConnectionHubState,
  updateCredentialVerificationDetails,
  updateForwardEmailQueue,
} from "../../redux/credentialVerification/credentialVerification";
import { triggerControlPanelRefresh } from "../../redux/controlPanel/controlPanel";
import { fetchCurrentConnections } from "../../redux/connections/connSlice";
import { fetchAgencyClients } from "../../redux/agencyConfig/agencyConfigUtil";
import {
  setCustomDataPeiodJobId,
  setJobId,
} from "../../redux/agencyDashboard/agencyDashboardSlice";

const useSocketOnIndex = (notify, handleNewChat) => {
  // console.log(userId);
  const socket = useRef(null);
  const userId = getLocalUserId();
  const token = getLocalJWT();
  const dispatch = useAppDispatch();
  // const { pathname } = useLocation();
  const navigate = useNavigate();

  // function notifyMe(message: string) {
  // if (!("Notification" in window)) {
  // // Check if the browser supports notifications
  // alert("This browser does not support desktop notification");
  // } else if (Notification.permission === "granted") {
  // // Check whether notification permissions have already been granted;
  // // if so, create a notification
  // const notification = new Notification(message);
  // // …
  // } else if (Notification.permission !== "denied") {
  // // We need to ask the user for permission
  // Notification.requestPermission().then((permission) => {
  // // If the user accepts, let's create a notification
  // if (permission === "granted") {
  // const notification = new Notification(message);
  // // …
  // }
  // });
  // }

  // // At last, if the user has denied notifications, and you
  // // want to be respectful there is no need to bother them anymore.
  // }

  useEffect(() => {
    socket.current = socketInit(userId.toString(), token);

    socket.current.on("connect", () => {
      socket.current.emit("JOIN_USER_ROOM", { roomId: `user_${userId}` });
      console.log("connected");
    });

    socket.current.on("disconnect", () => {
      console.log("socket disconnected"); // undefined
    });

    // socket.current.emit("JOIN_USER_ROOM", { roomId: `user_${userId}` });

    socket.current.on("JOIN_ACK", (message) => {
      console.log("CONNECTED FROM SERVER", socket.current.id);
    });

    socket.current.on("NEW_NOTIFICATION", (notification) => {
      console.log("new notification", notification);
      notify(notification);
      dispatch(addSingleNotification({ notification }));
    });

    socket.current.on(
      "GENERATED_INSIGHT",
      (message: { insights: DecisionInsights[]; success: boolean }) => {
        console.log("INSIGHT GENERATED", message);
        if (
          !window.location.pathname.toLowerCase().includes("pnltracker") &&
          !window.location.pathname.toLowerCase().includes("underwriting")
        ) {
          if (message.insights?.length === 0) {
            notify({
              notificationBody: {
                title: "Ideas Generation Failed",
                status: ToastifyStatus.failure,
                subTitle: "There has been a technical error.",
                buttonText: "Contact Support",
                buttonAction: () => {
                  navigate("/Support");
                },
              },
            });
            return;
          }

          notify({
            notificationBody: {
              title: "Ideas are Ready!",
              status: ToastifyStatus.success,
              subTitle: "Head to Boost tab and view them now.",
              buttonText: "View Ideas",
              buttonAction: () => {
                navigate("/boost");
              },
            },
          });
          dispatch(setInsightGenerated(true));
          dispatch(setRefreshDashboard(true));
        }

        dispatch(setRefreshDashboard(true));
      }
    );

    socket.current.on("REFRESH_FINANCE_STATEMENT", (message) => {
      console.log("REFRESH_FINANCE_STATEMENT", message);
      if (
        !message?.isInProgress &&
        message?.isCompleted &&
        !window.location.pathname.toLowerCase().includes("underwriting") &&
        !message?.isFirstTime
      ) {
        notify({
          notificationBody: {
            title: "Data Updated",
            status: ToastifyStatus.success,
            subTitle: "P&L Report has been updated with your latest changes.",
            // buttonText: "View Ideas",
            // buttonAction: () => {
            //   navigate("/boost");
            // },
          },
        });
      }
      dispatch(setRefreshFinanceStatement(true));
      dispatch(fetchProductMissingCosts());
      dispatch(getMissingCostItemsThunk());
      dispatch(setRefreshDashboard(true));
    });

    socket.current.on("PRODUCT_COST_UPDATED", async (message) => {
      console.log("PRODUCT_COST_UPDATED", message);
      if (
        !message?.isInProgress &&
        message?.isCompleted &&
        !window.location.pathname.toLowerCase().includes("underwriting")
      ) {
        notify({
          notificationBody: {
            title: "Product Costs Updated!",
            status: ToastifyStatus.success,
            subTitle: message?.message
              ? message?.message
              : "Your latest product costs have been fetched from Shopify.",
            // buttonText: "View Ideas",
            // buttonAction: () => {
            //   navigate("/boost");
            // },
          },
        });
      }
      dispatch(setRefreshFinanceStatement(true));
      // const data = await RestS.getDataFetchingStatus()
      // dispatch(changeDfStatus(data));
    });

    socket.current.on("NEW_CHANNEL", (message) => {
      console.log("NEW_CHANNEL", message);
      const channel = message["channel"] as Channel;

      notify(message["notification"]);
      dispatch(
        addSingleNotification({ notification: message["notification"] })
      );
      dispatch(addNewChannel({ channel: channel }));
    });

    socket.current.on("RESET_NOTIIFCATION_STATUS", (message) => {
      console.log("reset notification", message);
      dispatch(changeResetStatus({ type: message["type"], value: true }));
    });

    socket.current.on("DELETE_CHAT", (message) => {
      console.log("chat deleted", message);
      dispatch(deleteChannelFromNotification(message["channelId"]));
    });

    socket.current.on("DELETE_TRACKER", (message) => {
      console.log("trackers delete", message);
      dispatch(refreshAsyncTrackerSummary("dummy"));
      dispatch(deleteTrackerFromNotification({ trackerId: message }));
    });

    socket.current.on("ADD_TRACKER", (message) => {
      const tracker = message["tracker"] as TrackerWithRecipientNames;
      console.log("added tracker", tracker);
      dispatch(refreshAsyncTrackerSummary("dummy"));
      dispatch(addTrackerFromNotification({ tracker: tracker }));
    });

    socket.current.on("UPDATE_TRACKER", (message) => {
      dispatch(refreshAsyncTrackerSummary("dummy"));
      dispatch(updateTrackerFromNotification(message));
    });

    socket.current.on("notify_user", (data) => {
      dispatch(dataReceived({ status: false }));
      notify(
        {
          notificationBody: {
            title: data?.title,
            subTitle: data?.subTitle,
            status: data?.status,
          },
        },
        data?.error ? true : false
      );
    });

    socket.current.on("workspace_created", (data) => {
      // if (window.location.pathname.includes('workspaceStore'))
      //   navigate(`/Reportdetails/${data.reportId}`)
      console.log("workspace_created");
      dispatch(createWs({ status: false, reportId: data.reportId }));

      dispatch(setForceRefresh(true));

      // let hasToRedirect = localStorage.getItem("hasToRefresh");
      let count = localStorage.getItem("refreshCount")
        ? Number(localStorage.getItem("refreshCount"))
        : 0;
      localStorage.setItem("refreshCount", `${count - 1}`);

      notify({
        notificationBody: {
          title: data?.title,
          subTitle: data?.subTitle,
          status: data?.status,
        },
      });

      if (window.location.pathname?.toLowerCase() == "/reports") {
        dispatch(setRefreshMyWorkspace(true));
      }
    });

    socket.current.on("error_handle_customDataPeriod", (message) => {
      console.log("error_handle_customDataPeriod", message);
      dispatch(setError(message));
    });

    socket.current.on("refresh_permanent_customdp", (message) => {
      console.log("RESPONSE ON PERMANENT_CUSTOM_DP_SOCKET");
      dispatch(permanentToggleIsInProgress({}));
      window.location.reload();
    });

    socket.current.on("temporary_custom_dp", (message: any) => {
      console.log("RESPONSE ON TEMPORARY_CUSTOM_DP_SOCKET", message);

      dispatch(
        temporaryUpdate({
          reportId: message?.reportDetails?.id,
          data: {
            charts: message?.charts,
            pivotTable: message?.pivotTable,
            cards: message?.cards,
            jobDetails: message?.jobDetails,
            dataSource: message?.dataSource,
            dataPeriod: message?.dataPeriod,
            cohorts: message?.cohorts,
            accessTokenIds: message?.accessTokenIds,
          },
          jobId: message?.jobId,
        })
      );
    });

    socket.current.on("view_select", (message) => {
      console.log("RESPONSE ON VIEW_SELECT_SOCKET", message);
      dispatch(
        viewSelectRefreshToggle({
          jobId: message?.jobId,
          reportVersionId: message.reportVersionId,
          changeVersion: true,
          toggleIsInProgress: true,
        })
      );
    });

    socket.current.on("DATA_FETCHING_PROGRESS", (message) => {
      console.log("data fetching progress", message);
      dispatch(changeDfStatus(message));
      dispatch(setRefreshMetricsAndIdeas(true));
    });

    socket.current.on("ManualRefreshWorkspace", (message) => {
      dispatch(workspaceRefreshed({ status: false, isRefreshed: false }));

      dispatch(setRefreshDashboard(true));

      if (
        window.location.pathname ==
        `/ReportDetails/${message.reportId.toString()}`
      ) {
        notify(
          {
            notificationBody: {
              title: `Your browser will refresh to add more data to the ${message?.reportName} Workspace`,
              status: ToastifyStatus.success,
            },
          } as any,
          false,
          {
            showProgressBar: true,
            closeAction: () => {
              const time = new Date().getTime() + 10000;
              localStorage.setItem("refresed", "true");
              localStorage.setItem("refreshExpiry", time.toString());
              localStorage.setItem("refreshReportName", message?.reportName);
              window.location.reload();
            },
            autoCloseTime: 5000,
          }
        );
        // window.location.reload();
      }
    });

    socket.current.on("PC_JOURNEY_UPDATE", (message) => {
      dispatch(setRefreshPcHome(true));
    });

    socket.current.on("UW_JOURNEY_UPDATE", (message) => {
      console.log("UW_JOURNEY_UPDATE", message);
      dispatch(setRefreshUwHome(true));
    });

    socket.current.on("PRODUCT_COSTS_FETCHING", (message) => {
      console.log("product cost fetching status from socket", message);
      dispatch(setRefreshAccountUpdate(true));
      if (message["status"] == ProductFetchingProgress.success) {
        let isProductUpdate = message["isProductUpdate"]
          ? message["isProductUpdate"]
          : false;
        notify({
          notificationBody: {
            title: isProductUpdate ? "App Updated!" : "Data Fetching Complete!",
            status: ToastifyStatus.success,
            subTitle: "You can now update product costs.",
          },
        });
      }

      if (message["status"] == ProductFetchingProgress.failed) {
        notify({
          notificationBody: {
            title: "Shopify App Update Failed!",
            status: ToastifyStatus.failure,
            subTitle: "Contact Merito.",
          },
        });
      }
    });

    socket.current.on("BULK_UPDATE_COMPLETED", (message) => {
      console.log("BULK_UPDATE_COMPLETED", message);
      dispatch(setBulkUpdateStatus(false));
    });

    socket.current.on("DATA_FETCHING_PC", (message) => {
      dispatch(setRefreshAccountFetching(true));
      dispatch(setRefreshDashboard(true));
    });

    socket.current.on("CLOSE_BULK_UPDATE", (message) => {
      console.log("CLOSE_BULK_UPDATE", message, window.location.pathname);
      // dispatch(setRefreshAccountUpdate(true));

      if (window.location.pathname.includes("product-costs")) {
        console.log("close bulk modal");
        dispatch(setCloseBulkModal(true));
      }
    });

    socket.current.on("DASHBOARD_JOB_UPDATE", (message) => {
      console.log("DASHBOARD_JOB_UPDATE", message);
      dispatch(updateTemporaryJobDetails(message?.jobDetails));
    });

    socket.current.on("REFRESH_DASHBOARD", (message) => {
      console.log("REFRESH_DASHBOARD", message);
      dispatch(updateCredentialVerificationDetails(message));
    });

    socket.current.on("RPA_CREDENTIAL_VERIFICATION", (message) => {
      console.log(`Got message on RPA_CREDENTIAL_VERIFICATION: `, message);
      const isConnectionHubOpen =
        localStorage.getItem("isConnectionHubOpen") ?? "false";
      localStorage.setItem("validatingRPAExpiry", "0");
      console.log("isConnectionHubOpen", isConnectionHubOpen);

      if (isConnectionHubOpen == "true") {
        dispatch(
          updateCredentialVerificationDetails({
            ...message,
            isConnectionHubOpen: true,
          })
        );
      } else {
        dispatch(
          updateCredentialVerificationDetails({
            ...message,
            isConnectionHubOpen: false,
          })
        );
      }

      const ipName = message?.integrationPartner?.name;

      if (isConnectionHubOpen == "false") {
        // Send toastify
        if (message?.isCredentialValid) {
          notify(
            {
              notificationBody: {
                title: `${ipName} Account Connected!`,
                status: ToastifyStatus.success,
                subTitle: "Data fetching may take up to 45 minutes..",
              },
            },
            false,
            { autoCloseTime: 2000 }
          );
        } else {
          notify(
            {
              notificationBody: {
                title: `Couldn't Connect ${ipName}`,
                status: ToastifyStatus.failure,
                hideSubTitle: true,
                primaryButtonText: "Try Again",
                buttonAction: () => {
                  console.log("here", window.location.pathname);
                  if (window.location.pathname?.includes("/workspaceStore")) {
                    // If the user is on workspace store then open connection hub
                    console.log("Opening the Connection Hub");
                    dispatch(changeConnectionHubState({ open: true }));
                  } else {
                    // If the user is not on workspace store then redirect to workspaceStore with open connection hub
                    navigate(`/workspaceStore?ipName=${ipName}&success=false`);
                  }
                },
                background: "#FADBDB",
              },
            },
            true
          );
        }
      }
    });

    socket.current.on("REFRESH_STATEMENT_JOBS", (message) => {
      const currentPathName = window.location.pathname.toLowerCase();
      console.log("REFRESH_STATEMENT_JOBS", message);
      if (
        currentPathName.includes("underwriting") ||
        currentPathName.includes("monthlyreport")
      ) {
        dispatch(setRefreshStatementJobs(true));
      }
    });

    socket.current.on("REFRESH_GST_JOBS", (message) => {
      console.log("REFRESH_GST_JOBS", message);
      const currentPathName = window.location.pathname.toLowerCase();
      if (
        currentPathName.includes("underwriting") ||
        currentPathName.includes("monthlyreport")
      ) {
        dispatch(setRefreshGstJobs(true));
      }
    });

    socket.current.on("REFRESH_REVENUE_CLASSIFICATION", (message) => {
      console.log("REFRESH_REVENUE_CLASSIFICATION", message);
      const currentPathName = window.location.pathname.toLowerCase();
      if (currentPathName.includes("underwriting") && message?.fyYear) {
        dispatch(setRefreshRevenueClassification(message.fyYear));
      }
    });

    socket.current.on("REFRESH_FORM_DATA", (message) => {
      console.log("REFRESH_FORM_DATA", message);
      const currentPathName = window.location.pathname.toLowerCase();
      if (
        currentPathName.includes("underwriting") ||
        currentPathName.includes("monthlyreport")
      ) {
        // dispatch(setRefreshStatementJobs(true));
        dispatch(setRefreshFormData(true));
      }
    });

    socket.current.on("REFRESH_CONTROL_PANEL_USER_LIST", (message) => {
      console.log("REFRESH_CONTROL_PANEL_USER_LIST", message);
      if (window.location.pathname.includes("Settings")) {
        triggerControlPanelRefresh();
      }
      dispatch(fetchAgencyClients({}));
    });

    socket.current.on("EMAIL_FORWARD_CONFIRMATION", (message) => {
      console.log("EMAIL_FORWARD_CONFIRMATION", message);
      if (message?.isEmailForwarded) {
        dispatch(
          updateForwardEmailQueue({
            email: message?.accountId,
            jobDetails: {
              status: forwardEmailQueueStatus.success,
              error: null,
            },
          })
        );
      } else {
        dispatch(
          updateForwardEmailQueue({
            email: message?.accountId,
            jobDetails: {
              status: forwardEmailQueueStatus.failure,
              error: message?.emailForwardError,
            },
          })
        );
      }
      // notify({
      //   notificationBody: {
      //     title: message?.title,
      //     status: message?.status,
      //     subTitle: message?.subTitle,
      //   },
      // });
    });

    socket.current.on("AG_DASHBOARD_STATUS", (message) => {
      console.log("AG_DASHBOARD_STATUS", message);
      dispatch(setJobId(message?.jobId));
      dispatch(setCustomDataPeiodJobId(message?.jobId));
    });

    return () => {
      socket.current.disconnect();
    };
  }, []);

  const handleJoinRoom = (roomIds: string[]) => {
    console.log("roomIds", roomIds);
    if (socket.current && roomIds.length > 0) {
      socket.current.emit("JOIN_CHANNEL", { channelId: roomIds });
    }
  };

  return {
    socket: socket.current,
    handleJoinRoom,
  };
};

export default useSocketOnIndex;
